import { HTMLAttributes, useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, Modal, Popover } from 'antd';
import {
  DeleteOutlined,
  EllipsisOutlined,
  FormOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import StatusSwitch from 'components/StatusSwitch';
import useSlide from 'views/Conversation/Sider/useSlide';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import EditorPreviewer from 'components/EditorPreviewer';
import { getTextLength } from '../../components/ContextEditor';
import { CountryFlagIcon } from 'components/CountryFlagIcon';

export const Container = styled.div`
  border: solid 1px var(--fett-border-default);
  border-radius: 2px;
`;

export const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  border-bottom: solid 1px var(--fett-border-default);
`;

export const Tag = styled.div`
  height: 24px;
  padding: 0 8px;
  color: var(--fett-text-tertiary);
  background: var(--fett-bg-app);
  border-radius: 2px;
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  > h3 {
    margin-bottom: 0;
    font-size: 16px;
    color: var(--fett-text-primary);
  }
  > * {
    margin-right: 8px;
  }
`;

export const Content = styled.div`
  display: flex;
  padding: 16px 24px;
  dl {
    margin-bottom: 0;
  }
  dt {
    margin-bottom: 8px;
    color: var(--fett-text-primary);
    > * {
      margin-left: 8px;
    }
  }
`;

export const ContentCenter = styled.dl`
  flex: 1 0 0;
`;

export const ContentRight = styled.dl`
  flex: 0 0 350px;
  margin-left: 50px;
  overflow: hidden;
`;

export const Index = styled.span<{ disabled?: boolean }>`
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 10px;
  text-align: center;
  color: ${({ disabled = false }) => (disabled ? '#D9DDE4' : '#A1A3A8')};
  background: var(--fett-bg-app);
  cursor: ${({ disabled = false }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const TextContent = styled.dd`
  > p {
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    word-break: break-all;
    > div > div {
      height: auto;
    }
  }
  > div {
    height: 1px;
    margin: 14px 0;
    border-top: dashed 1px #ebebeb;
  }
`;

export const Store = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
  overflow: hidden;
  > span {
    flex: 0 0 18px;
    margin-right: 8px;
  }
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const More = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  > span {
    width: 16px;
    height: 16px;
    line-height: 16px;
    margin-left: 8px;
    text-align: center;
    border-radius: 8px;
    background: var(--fett-chat-bubble-agent);
    color: #6695ff;
  }
`;

export const PopoverContent = styled.div`
  width: 350px;
  max-height: 300px;
  margin: -12px -16px;
  padding: 12px 16px;
  overflow-x: hidden;
  overflow-y: auto;
`;

export interface TaskProps extends HTMLAttributes<HTMLDivElement> {
  task: AutoEmit.Refund.Task;
  onEdit: (task: AutoEmit.Refund.Task) => void;
  onDelete: (task: AutoEmit.Refund.Task) => Promise<any>;
  onEnableUpdate: (checked: boolean, task: AutoEmit.Refund.Task) => Promise<any>;
}

function Task(props: TaskProps) {
  const { task, onEdit, onDelete, onEnableUpdate, ...otherProps } = props;

  const {
    platform,
    scenarioType,
    name,
    enabled,
    executeContext: originExecuteContext,
    region,
    stores,
  } = task;

  const { t } = useTranslate();

  const executeContext = useMemo(() => {
    return originExecuteContext.map(({ content, translatedContent }) => {
      return {
        content: mustache2SlateValue(content),
        translatedContent: mustache2SlateValue(translatedContent),
      };
    });
  }, [originExecuteContext]);

  const tagLabel = {
    CANCEL_TRADE_FOR_ADDRESS: t('refundSetting.修改收货地址'),
    CANCEL_TRADE_FOR_INFO: t('refundSetting.修改订单信息'),
    CANCEL_TRADE_FOR_VOUCHER: t('refundSetting.修改优惠券'),
  }[scenarioType];

  const isStoreAliasFirst = useIsStoreAliasFirst(platform, region);

  const storeRender = (store: AutoEmit.Store) => {
    const { storeId, storeName, alias } = store;

    return (
      <Store key={storeId}>
        <CountryFlagIcon region={region} />
        <div>{isStoreAliasFirst ? alias || storeName : storeName}</div>
      </Store>
    );
  };

  const { index, pre, next, preDisabled, nextDisabled } = useSlide({
    count: 1,
    length: executeContext.length,
  });

  const {
    [index]: { content, translatedContent },
  } = executeContext;

  const contentLength = getTextLength(content);
  const translatedContentLength = getTextLength(translatedContent);

  return (
    <Container {...otherProps}>
      <Toolbar>
        <Title>
          <Tag>{tagLabel}</Tag>
          <h3>{name}</h3>
          <StatusSwitch
            checkedChildren={t('refundSetting.启用中')}
            unCheckedChildren={t('refundSetting.未启用')}
            checked={enabled}
            onChange={(checked) => onEnableUpdate(checked, task)}
          />
        </Title>
        <div>
          <Button type="text" icon={<FormOutlined />} onClick={() => onEdit(task)}>
            {t('phrase.编辑')}
          </Button>
          <StatusButton
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => {
              return new Promise((resolve) => {
                const { destroy } = Modal.confirm({
                  title: t('refundSetting.您确定删除该场景吗？'),
                  onOk: () => {
                    destroy();
                    onDelete(task).then(resolve).catch(resolve);
                  },
                  onCancel: (e) => {
                    destroy();
                    resolve(e);
                  },
                });
              });
            }}
          >
            {t('phrase.删除')}
          </StatusButton>
        </div>
      </Toolbar>
      <Content>
        <ContentCenter>
          <dt>
            {`${t('refundSetting.回复内容')}${
              executeContext.length > 1 ? `(${index + 1}/${executeContext.length})` : ''
            }`}
            {executeContext.length > 1 && (
              <>
                <Index disabled={preDisabled} onClick={pre}>
                  <LeftOutlined />
                </Index>
                <Index disabled={nextDisabled} onClick={next}>
                  <RightOutlined />
                </Index>
              </>
            )}
          </dt>
          <TextContent>
            {translatedContentLength > 0 && (
              <p>
                <EditorPreviewer value={translatedContent} />
              </p>
            )}
            {contentLength && translatedContentLength ? <div /> : null}
            {contentLength > 0 && (
              <p>
                <EditorPreviewer value={content} />
              </p>
            )}
          </TextContent>
        </ContentCenter>
        <ContentRight>
          <dt>{t('refundSetting.适用店铺')}</dt>
          <dd>
            {stores.slice(0, 3).map(storeRender)}
            {stores.length > 3 && (
              <Popover
                content={<PopoverContent>{stores.slice(3).map(storeRender)}</PopoverContent>}
              >
                <More>
                  {t('refundSetting.更多x家店铺', { x: stores.length - 3 })}
                  <span>
                    <EllipsisOutlined />
                  </span>
                </More>
              </Popover>
            )}
          </dd>
        </ContentRight>
      </Content>
    </Container>
  );
}

export default Task;
